import { InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import { maxLength, required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/wage-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistWage } from 'src/store/actions/wage-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDropdown } from '../../../../../components/redux-form/fields/FieldDropdown';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';

export interface WageFormData {
    id: string | number;
    providerDropDown: any;
    periodStart: string;
    periodEnd: string;
    workedDays: number;
    workedHours: number;
    amount: number;
}

interface WageFormProps {
    providers: SelectObject[];
    providerSelect?: string | number;
    descSalary?: string;
}

type InjectedProps = InjectedFormProps<WageFormData, WageFormProps>;

const validators: Validators<WageFormData> = {
    providerDropDown: [required({ message: 'Provider is required.' }), maxLength({ max: 255 })],
    amount: [required({ message: 'Amount is required.' })],
    periodStart: [required({ message: 'From Date is required.' })],
    periodEnd: [required({ message: 'To Date is required.' })],
};

const WageForm: React.FunctionComponent<InjectedProps & WageFormProps> = ({
    handleSubmit,
    error,
    dirty,
    descSalary,
    providers,
    providerSelect,
    submitting,
    initialValues,
}: InjectedProps & WageFormProps) => {
    const isNew = !initialValues || !initialValues.id;

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <FieldInput
                name="id"
                type="hidden"
                wrapperConfig={{
                    classes: {
                        inputWrapper: 'm-0',
                    },
                }}
            />
            <FieldDropdown
                name="providerDropDown"
                data={providers}
                textField={'label'}
                valueField={'value'}
                validate={validators.providerDropDown}
                wrapperConfig={{
                    label: 'Provider: *',
                }}
            />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-3">
                        <div className="row">
                            <div className="col-md-6 pl-0">
                                <FieldDate
                                    name="periodStart"
                                    type="text"
                                    date={true}
                                    validate={validators.periodStart}
                                    className="mr-5"
                                    wrapperConfig={{
                                        label: 'From: *',
                                    }}
                                />
                            </div>
                            <div className="col-md-6 pl-0">
                                <FieldDate
                                    name="periodEnd"
                                    type="text"
                                    validate={validators.periodEnd}
                                    date={true}
                                    className="mr-5"
                                    wrapperConfig={{
                                        label: 'To: *',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FieldInput
                name="amount"
                type="number"
                validate={validators.amount}
                wrapperConfig={{
                    label: 'Amount, $ *',
                }}
            />
            {descSalary ? <div className={classNames(styles.wage, 'mt-2 mb-2')}>{descSalary}</div> : null}
            <FieldInput
                name="workedDays"
                type="number"
                wrapperConfig={{
                    label: 'Number of worked days:',
                }}
            />
            <FieldInput
                name="workedHours"
                type="number"
                wrapperConfig={{
                    label: 'Number of hours worked:',
                }}
            />
            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success  mr-2')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link
                    className={classNames('btn', 'btn-danger')}
                    to={Router.generate(routes.WAGES_LIST, { param: '' })}
                >
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<WageFormData, WageFormProps>({
    form: 'wage-form',
    enableReinitialize: true,
    onSubmit: async (data: WageFormData, dispatch: Dispatch<any>, props: WageFormProps) => {
        const endTimeT = new Date(data.periodEnd);
        endTimeT.setHours(23);
        const startT = new Date(data.periodStart);

        if (endTimeT.getTime() <= startT.getTime()) {
            toast.error('Incorrect time!');
        } else {
            await asyncPersistWage(data, dispatch);
        }
    },
    onSubmitSuccess() {},

    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(WageForm);
